import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, GraduationCap, BookOpen, Award, ShieldCheck } from "lucide-react";
import { SITE, COURSES } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";

const COURSE_ICONS = [BookOpen, GraduationCap, Award];

export default function TaxSchool() {
  return (
    <div data-testid="tax-school-page">
      <PageHero
        eyebrow="TRJ Tax School"
        title="Train. Certify. Build a Tax Business."
        subtitle="Professional tax education designed for new and experienced preparers — covering fundamentals, ethics, business setup, and operations. Complete the certification track to launch your own tax practice."
        image={SITE.assets.team}
      >
        <a href="#courses" className="trj-btn-green" data-testid="tax-school-hero-courses-btn">
          View Courses <ArrowRight className="w-4 h-4" />
        </a>
        <ScheduleConsultationButton variant="gold" testid="tax-school-hero-talk-btn" label="Talk to an Advisor" />
      </PageHero>

      {/* INTRO */}
      <section className="bg-white py-20" data-testid="tax-school-intro">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="trj-eyebrow mb-4">Curriculum Overview</div>
          <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
            Two focused courses. One complete certification program.
          </h2>
          <p className="mt-5 text-[var(--trj-muted)] leading-relaxed max-w-3xl mx-auto">
            Start with the fundamentals, learn how to build a tax business, or take both as a bundled
            certification track — including ethics, templates, checklists, and a Certificate of Completion.
          </p>
        </div>
      </section>

      {/* COURSE CARDS */}
      <section id="courses" className="bg-[var(--trj-light)] py-20 md:py-28 scroll-mt-28" data-testid="tax-school-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {COURSES.map((c, i) => {
              const Ico = COURSE_ICONS[i] || BookOpen;
              const bundle = c.bundle;
              return (
                <div
                  key={c.slug}
                  data-testid={`course-card-${c.slug}`}
                  className={
                    bundle
                      ? "trj-card-premium flex flex-col"
                      : "bg-white border border-gray-200 p-8 flex flex-col transition-shadow hover:shadow-xl"
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className={`text-[10px] tracking-[0.25em] uppercase font-bold ${bundle ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"}`}>
                      {c.pill}
                    </div>
                    <div className={`w-10 h-10 flex items-center justify-center ${bundle ? "bg-[var(--trj-gold)] text-[var(--trj-navy)]" : "bg-[var(--trj-navy)] text-[var(--trj-gold)]"}`}>
                      <Ico className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className={`font-serif-trj text-3xl mt-5 leading-tight ${bundle ? "text-white" : "text-[var(--trj-navy)]"}`}>{c.title}</h3>
                  <p className={`mt-3 text-[14px] leading-relaxed ${bundle ? "text-white/80" : "text-[var(--trj-muted)]"}`}>{c.description}</p>

                  <div className={`mt-5 pt-5 border-t ${bundle ? "border-white/20" : "border-gray-200"}`}>
                    <div className={`text-xs uppercase tracking-widest ${bundle ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"} font-bold`}>Includes</div>
                    <ul className="mt-3 space-y-2">
                      {c.includes.map((it) => (
                        <li key={it} className={`flex items-start gap-2 text-[13px] ${bundle ? "text-white/85" : "text-[var(--trj-navy)]"}`}>
                          <CheckCircle2 className={`w-4 h-4 mt-0.5 ${bundle ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"}`} /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto pt-8 flex items-end justify-between">
                    <div>
                      <div className={`text-xs uppercase tracking-widest ${bundle ? "text-white/60" : "text-[var(--trj-muted)]"}`}>Tuition</div>
                      <div className={`font-serif-trj text-4xl ${bundle ? "text-[var(--trj-gold)]" : "text-[var(--trj-navy)]"}`}>{c.price}</div>
                    </div>
                  </div>
                  <a
                    href={c.paypalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-5 ${bundle ? "trj-btn-gold" : "trj-btn-green"} text-sm justify-center w-full`}
                    data-testid={`course-enroll-${c.slug}`}
                  >
                    Enroll Now <ArrowRight className="w-4 h-4" />
                  </a>
                  {c.applicationUrl && (
                    <Link
                      to={c.applicationUrl}
                      className="trj-btn-outline-navy text-sm justify-center w-full mt-2"
                      data-testid={`course-apply-${c.slug}`}
                    >
                      Apply via Application Form
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="bg-white py-20" data-testid="tax-school-benefits">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="trj-eyebrow mb-3">What You Receive</div>
            <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">Practical training. Real-world tools.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {[
              { t: "Professional Curriculum", d: "Built from 25+ years of real tax & business experience." },
              { t: "Ethics Training", d: "Industry-standard ethics content woven through every track." },
              { t: "Templates & Checklists", d: "Production-ready intake forms, workflows, and SOPs." },
              { t: "Certificate of Completion", d: "Awarded upon successful completion of the certification bundle." },
            ].map((it) => (
              <div key={it.t} className="bg-white p-6">
                <div className="font-serif-trj text-xl text-[var(--trj-navy)]">{it.t}</div>
                <p className="text-sm text-[var(--trj-muted)] mt-2 leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--trj-navy)] py-20 text-white text-center" data-testid="tax-school-cta">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif-trj text-3xl md:text-5xl leading-tight">
            Ready to enroll?
          </h2>
          <p className="mt-5 text-white/80">Schedule a brief call to discuss the right track for you.</p>
          <div className="mt-8 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton variant="gold" testid="tax-school-cta-schedule-btn" label="Schedule Consultation" />
            <Link to="/contact" className="trj-btn-outline-white" data-testid="tax-school-cta-contact-btn">
              Ask a Question
            </Link>
          </div>
        </div>
      </section>

      {/* Secure-checkout trust strip */}
      <section className="bg-[var(--trj-navy)] border-t border-white/10" data-testid="tax-school-secure-strip">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center gap-3 text-[12px] text-white/70">
          <ShieldCheck className="w-3.5 h-3.5 text-[var(--trj-gold)]" />
          Encrypted, PCI-compliant checkout powered by PayPal · Pay with PayPal or any major credit/debit card
        </div>
      </section>
    </div>
  );
}
